import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,

} from '@mui/material';
import {
  Home as HomeIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Handshake as HandshakeIcon,
  Payment as PaymentIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Work as WorkIcon,
  Star as StarIcon,
  PlayCircle as PlayCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import './GuideUtilisation.css';

const GuideUtilisation = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(false);

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const organizationSteps = [
    {
      id: 1,
      title: 'Créer un compte',
      description: 'Inscrivez-vous en tant qu\'organisation sportive et complétez votre profil.',
      icon: <AccountCircleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
    },
    {
      id: 2,
      title: 'Publier une mission',
      description: 'Décrivez votre besoin, définissez le budget et les compétences requises.',
      icon: <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
    },
    {
      id: 3,
      title: 'Recevoir des candidatures',
      description: 'Les professionnels qualifiés postulent à votre mission.',
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
    },
    {
      id: 4,
      title: 'Sélectionner et contractualiser',
      description: 'Choisissez le meilleur profil et signez le contrat en ligne.',
      icon: <HandshakeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
    },
    {
      id: 5,
      title: 'Gérer les paiements et évaluations',
      description: 'Suivez l\'avancement, effectuez les paiements et laissez un avis.',
      icon: <PaymentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
    }
  ];

  const professionalSteps = [
    {
      id: 1,
      title: 'Créer un profil',
      description: 'Inscrivez-vous et mettez en valeur vos compétences sportives.',
      icon: <PersonIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
    },
    {
      id: 2,
      title: 'Parcourir les missions',
      description: 'Explorez les opportunités qui correspondent à votre expertise.',
      icon: <SearchIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
    },
    {
      id: 3,
      title: 'Postuler aux missions',
      description: 'Envoyez votre candidature avec une proposition personnalisée.',
      icon: <SendIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
    },
    {
      id: 4,
      title: 'Réaliser la mission',
      description: 'Mettez vos compétences au service de l\'organisation.',
      icon: <WorkIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
    },
    {
      id: 5,
      title: 'Être rémunéré et évalué',
      description: 'Recevez votre paiement et construisez votre réputation.',
      icon: <StarIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
    }
  ];

  const faqData = [
    {
      id: 'faq1',
      question: 'Comment créer un compte sur Mission Sport ?',
      answer: 'Cliquez sur "S\'inscrire" en haut de la page, choisissez votre type de profil (Organisation ou Professionnel), puis remplissez le formulaire d\'inscription. Vous recevrez un email de confirmation pour activer votre compte.'
    },
    {
      id: 'faq2',
      question: 'Comment sont gérés les paiements ?',
      answer: 'Les paiements sont sécurisés via Stripe et PayPal. L\'organisation effectue le paiement qui est mis en séquestre jusqu\'à la validation de la mission. Le professionnel reçoit ensuite son paiement automatiquement.'
    },
    {
      id: 'faq3',
      question: 'Est-ce que les missions sont vérifiées par Mission Sport ?',
      answer: 'Oui, toutes les missions publiées sont vérifiées par notre équipe avant d\'être mises en ligne. Nous vérifions la légitimité de l\'organisation et la cohérence de la mission proposée.'
    },
    {
      id: 'faq4',
      question: 'Quels sont les frais de la plateforme ?',
      answer: 'Mission Sport prélève une commission de 5% sur chaque transaction réussie. Cette commission couvre les frais de plateforme, la sécurisation des paiements et le support client.'
    },
    {
      id: 'faq5',
      question: 'Comment puis-je contacter le support ?',
      answer: 'Vous pouvez nous contacter via le formulaire de contact, par email à support@missionsport.com, ou directement depuis votre espace personnel via la messagerie intégrée.'
    }
  ];

  const StepCard = ({ step, index }) => (
    <Card 
      className="step-card"
      sx={{ 
        height: '100%',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Box 
            sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              bgcolor: 'grey.100', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            {step.icon}
          </Box>
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ fontWeight: 600, mb: 1 }}
          >
            {index + 1}. {step.title}
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          {step.description}
        </Typography>
      </CardContent>
    </Card>
  );

  const VideoPlaceholder = () => (
    <Paper 
      elevation={3}
      sx={{ 
        position: 'relative',
        paddingTop: '56.25%', // 16:9 aspect ratio
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'grey.900'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: 'white'
        }}
      >
        <PlayCircleIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Tutoriel vidéo</Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>Découvrez Mission Sport en 3 minutes</Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      <Helmet>
        <title>Guide d'utilisation - Mission Sport</title>
        <meta 
          name="description" 
          content="Découvrez comment utiliser Mission Sport pour connecter organisations sportives et professionnels indépendants. Guide complet étape par étape." 
        />
        <meta property="og:title" content="Guide d'utilisation - Mission Sport" />
        <meta 
          property="og:description" 
          content="Découvrez comment utiliser Mission Sport pour connecter organisations sportives et professionnels indépendants. Guide complet étape par étape." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://missionsport.com/guide-utilisation" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Guide d'utilisation - Mission Sport" />
        <meta 
          name="twitter:description" 
          content="Découvrez comment utiliser Mission Sport pour connecter organisations sportives et professionnels indépendants." 
        />
      </Helmet>

      <div className="guide-utilisation">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4 }}>
            <Link 
              component={RouterLink} 
              to="/" 
              color="inherit" 
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Accueil
            </Link>
            <Typography color="text.primary">Guide d'utilisation</Typography>
          </Breadcrumbs>

          {/* Header Section */}
          <Box className="guide-header" sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Guide d'utilisation
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                color: 'text.secondary',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.4,
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              Connecter les talents, créer les opportunités
            </Typography>
            
            {/* Hero Image */}
            <Box sx={{ mb: 4 }}>
              <img 
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                alt="Équipe sportive en action"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
            
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.6,
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Mission Sport est la plateforme qui révolutionne la mise en relation 
              entre organisations sportives et professionnels indépendants. 
              Découvrez comment tirer le meilleur parti de notre plateforme.
            </Typography>
          </Box>

          {/* Organizations Section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <BusinessIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography 
                variant="h2" 
                component="h2" 
                sx={{ 
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Pour les Organisations sportives
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                Trouvez facilement les professionnels dont vous avez besoin pour vos projets sportifs
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {organizationSteps.map((step, index) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={step.id}>
                  <StepCard step={step} index={index} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 8 }} />

          {/* Professionals Section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <PersonIcon sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
              <Typography 
                variant="h2" 
                component="h2" 
                sx={{ 
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Pour les Professionnels du sport
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                Développez votre activité en trouvant des missions qui correspondent à vos compétences
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {professionalSteps.map((step, index) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={step.id}>
                  <StepCard step={step} index={index} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 8 }} />

          {/* Video Tutorial Section */}
          <Box sx={{ mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                textAlign: 'center',
                fontWeight: 600,
                mb: 4,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Tutoriel vidéo
            </Typography>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <VideoPlaceholder />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  Découvrez Mission Sport en action
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Cette vidéo vous présente les fonctionnalités principales de la plateforme 
                  et vous guide pas à pas dans vos premiers pas sur Mission Sport.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Durée : 3 minutes • Mise à jour : Janvier 2024
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 8 }} />

          {/* FAQ Section */}
          <Box sx={{ mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                textAlign: 'center',
                fontWeight: 600,
                mb: 4,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Questions fréquentes
            </Typography>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              {faqData.map((faq) => (
                <Accordion 
                  key={faq.id}
                  expanded={expandedFAQ === faq.id}
                  onChange={handleFAQChange(faq.id)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${faq.id}-content`}
                    id={`${faq.id}-header`}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>

          {/* CTA Section */}
          <Box 
            className="cta-section"
            sx={{ 
              textAlign: 'center',
              py: 6,
              px: 4,
              borderRadius: 4,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
            }}
          >
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                fontWeight: 600,
                mb: 3,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Prêt à commencer ?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: 'text.secondary',
                fontSize: '1.1rem',
                maxWidth: 500,
                mx: 'auto'
              }}
            >
              Rejoignez dès maintenant la communauté Mission Sport et découvrez 
              de nouvelles opportunités dans le monde du sport.
            </Typography>
            
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Button 
                  component={RouterLink}
                  to="/register?type=organization"
                  variant="contained" 
                  size="large"
                  startIcon={<BusinessIcon />}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    width: '100%',
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  Je suis une organisation
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button 
                  component={RouterLink}
                  to="/register?type=professional"
                  variant="contained" 
                  size="large"
                  startIcon={<PersonIcon />}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    width: '100%',
                    bgcolor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.dark'
                    }
                  }}
                >
                  Je suis un professionnel
                </Button>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Besoin d'aide ? Consultez nos autres ressources :
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link component={RouterLink} to="/aide" sx={{ textDecoration: 'none' }}>
                  Centre d'aide
                </Link>
                <Link component={RouterLink} to="/missions" sx={{ textDecoration: 'none' }}>
                  Voir les missions
                </Link>
                <Link component={RouterLink} to="/contact" sx={{ textDecoration: 'none' }}>
                  Nous contacter
                </Link>
              </Box>
            </Box>
          </Box>
        </Container>
      </div>
    </>
  );
};

export default GuideUtilisation;